<template>
  <div class="map-location-picker">
    <div class="search-box">
      <van-field v-model="searchQuery" :placeholder="$t('placeholder_search_location')" @keyup.enter="searchLocation"
        style="border-radius: 4px;">
        <template #button>
          <van-button size="small" type="primary" @click="searchLocation">{{ $t('search') }}</van-button>
        </template>
      </van-field>
      <!-- 搜索结果列表 -->
      <div v-if="showSearchResults && searchResults.length > 0" class="search-results">
        <div v-for="(result, index) in searchResults" :key="index" @click="selectSearchResult(result)"
          class="search-result-item" @mouseover="$event.currentTarget.style.background = '#F7F5F2'"
          @mouseout="$event.currentTarget.style.background = 'white'">
          <div style="font-size: 14px; color: #1A1A1A; margin-bottom: 4px;">{{ result.display_name }}</div>
          <div style="font-size: 12px; color: #8A8A8A;">
            {{ $t('coords_display', {lng: result.lon.toFixed(6), lat: result.lat.toFixed(6)}) }}
          </div>
        </div>
      </div>
    </div>
    <div ref="mapContainer" class="map-container"></div>
  </div>
</template>

<script>
import axios from 'axios'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { Field, Button, Toast } from 'vant'
import Vue from 'vue'

Vue.use(Field)
Vue.use(Button)
Vue.use(Toast)

export default {
  name: 'MapLocationPicker',
  props: {
    width: {
      type: String,
      default: '100%'
    },
    height: {
      type: String,
      default: '400px'
    },
    // 是否自动获取用户位置
    autoGetLocation: {
      type: Boolean,
      default: true
    },
    // 地图初始中心点 [纬度, 经度]
    center: {
      type: Array,
      default: () => [39.9042, 116.4074] // 北京
    },
    // 地图初始缩放级别
    zoom: {
      type: Number,
      default: 13
    }
  },
  data() {
    return {
      map: null,
      userLocationMarker: null,
      clickedLocationMarker: null,
      searchResults: [],
      searchQuery: '',
      showSearchResults: false,
      userLat: null,
      userLng: null,
      clickedLat: null,
      clickedLng: null,
      isGeocoding: false // 是否正在反向地理编码
    }
  },
  mounted() {
    // 使用 ResizeObserver 监听容器尺寸变化，等容器有实际尺寸后再初始化地图
    this.observeContainerResize()
  },
  beforeDestroy() {
    // 清理 ResizeObserver
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    }
    // 清理地图实例
    if (this.map) {
      this.map.remove()
      this.map = null
    }
  },
  methods: {
    // 监听容器尺寸变化
    observeContainerResize() {
      if (!this.$refs.mapContainer) return

      this.resizeObserver = new ResizeObserver((entries) => {
        const entry = entries[0]
        if (entry) {
          const { width, height } = entry.contentRect
          // 当容器有实际尺寸（非0）且地图未初始化时，初始化地图
          if (width > 0 && height > 0 && !this.map) {
            this.initMap()
          } else if (width > 0 && height > 0 && this.map) {
            // 如果地图已存在但尺寸变化，刷新地图
            this.map.invalidateSize()
          }
        }
      })

      this.resizeObserver.observe(this.$refs.mapContainer)
    },
    // 初始化地图
    initMap() {
      if (this.map) {
        return
      }

      // 检查容器是否有实际尺寸
      const container = this.$refs.mapContainer
      if (!container || container.offsetWidth === 0 || container.offsetHeight === 0) {
        console.log('Map container has no size yet, waiting...')
        return
      }

      // 创建地图实例
      this.map = L.map(container).setView(this.center, this.zoom)

      // 添加 CartoDB Positron 图层（无需 API Key，支持全球访问）
      L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
        maxZoom: 19,
        subdomains: 'bcd'
      }).addTo(this.map)

      // 点击地图获取经纬度
      this.map.on('click', (e) => {
        this.onMapClick(e)
      })

      // 尝试获取用户当前位置
      if (this.autoGetLocation) {
        this.getUserLocation()
      }
    },
    // 获取用户当前位置
    async getUserLocation() {
      if (!navigator.geolocation) {
        Toast.fail(this.$t('toast_no_geolocation'))
        return
      }

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const lat = position.coords.latitude
          const lng = position.coords.longitude
          this.userLat = lat
          this.userLng = lng

          // 在地图上添加用户位置标记
          this.addUserLocationMarker(lat, lng)

          // 将地图中心移动到用户位置
          this.map.setView([lat, lng], 15)

          // 模拟点击当前位置，展示带确认按钮的气泡
          await this.showLocationConfirmation(lat, lng, true)
        },
        (error) => {
          console.error('Failed to get location:', error)
          Toast.fail(this.$t('toast_location_permission_denied'))
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      )
    },
    // 添加用户位置标记
    addUserLocationMarker(lat, lng) {
      // 如果已存在用户位置标记，先移除
      if (this.userLocationMarker) {
        this.map.removeLayer(this.userLocationMarker)
      }

      // 创建绿色标记表示用户位置
      const userIcon = L.divIcon({
        className: 'user-location-marker',
        html: '<div style="background-color: #E8543A; width: 20px; height: 20px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 5px rgba(0,0,0,0.3);"></div>',
        iconSize: [20, 20],
        iconAnchor: [10, 10]
      })

      this.userLocationMarker = L.marker([lat, lng], { icon: userIcon }).addTo(this.map)
    },
    // 展示位置确认气泡（通用方法）
    // preloadedAddress: 如果已知道地址，直接传入避免反向地理编码
    async showLocationConfirmation(lat, lng, isUserLocation = false, preloadedAddress = null) {
      this.clickedLat = lat
      this.clickedLng = lng

      // 如果已存在点击标记，先移除
      if (this.clickedLocationMarker) {
        this.map.removeLayer(this.clickedLocationMarker)
      }

      // 创建标记（用户位置用绿色，点击位置用红色，搜索结果用蓝色）
      let markerColor = '#E05252'
      let markerClass = 'click-location-marker'
      if (isUserLocation === true) {
        markerColor = '#E8543A'
        markerClass = 'user-location-marker'
      } else if (isUserLocation === 'search') {
        markerColor = '#3DAECF'
        markerClass = 'search-location-marker'
      }

      const clickIcon = L.divIcon({
        className: markerClass,
        html: `<div style="background-color: ${markerColor}; width: 20px; height: 20px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 5px rgba(0,0,0,0.3);"></div>`,
        iconSize: [20, 20],
        iconAnchor: [10, 10]
      })

      this.clickedLocationMarker = L.marker([lat, lng], { icon: clickIcon }).addTo(this.map)

      // 存储当前组件实例的引用
      const self = this

      // 创建带确认按钮的 popup 内容
      const popupContent = document.createElement('div')

      const addressDiv = document.createElement('div')
      addressDiv.style.cssText = 'font-size: 14px; color: #1A1A1A; margin-bottom: 8px; max-width: 200px; word-wrap: break-word;'

      const coordsDiv = document.createElement('div')
      coordsDiv.style.cssText = 'font-size: 12px; color: #8A8A8A; margin-bottom: 12px;'
      coordsDiv.textContent = this.$t('coords_display', {lng: lng.toFixed(6), lat: lat.toFixed(6)})

      const confirmBtn = document.createElement('button')
      // 更新按钮状态的函数
      const updateButtonState = (disabled, text) => {
        confirmBtn.disabled = disabled
        confirmBtn.style.cssText = disabled
          ? 'background-color: #ccc; color: white; border: none; padding: 6px 16px; border-radius: 4px; cursor: not-allowed; font-size: 14px; width: 100%;'
          : 'background-color: #34A853; color: white; border: none; padding: 6px 16px; border-radius: 4px; cursor: pointer; font-size: 14px; width: 100%;'
        confirmBtn.textContent = text
      }

      confirmBtn.onclick = function() {
        if (!self.isGeocoding) {
          self.confirmLocationSelection(lat, lng, addressDiv.textContent)
        }
      }

      popupContent.appendChild(addressDiv)
      popupContent.appendChild(coordsDiv)
      popupContent.appendChild(confirmBtn)

      // 绑定并打开 popup
      this.clickedLocationMarker.bindPopup(popupContent).openPopup()

      // 获取地址
      let address = preloadedAddress
      if (!address) {
        // 设置正在加载状态，禁用按钮
        this.isGeocoding = true
        updateButtonState(true, this.$t('loading'))
        addressDiv.textContent = this.$t('loading_address')

        // 调用反向地理编码获取地址
        address = this.$t('coords_display', {lng: lng.toFixed(6), lat: lat.toFixed(6)})
        try {
          const response = await axios.get('/api/map/reverse-geocode', {
            params: { lat: lat, lon: lng }
          })

          if (response.data.code === 0) {
            const result = response.data.data
            if (result && result.display_name) {
              address = result.display_name
            }
          }
        } catch (error) {
          console.error('Reverse geocoding error:', error)
        } finally {
          // 无论成功或失败，都结束加载状态
          this.isGeocoding = false
          addressDiv.textContent = address
          updateButtonState(false, this.$t('confirm'))
        }
      } else {
        // 有预加载地址，直接显示
        addressDiv.textContent = address
        updateButtonState(false, this.$t('confirm'))
      }
    },
    // 处理地图点击事件
    onMapClick(e) {
      const lat = e.latlng.lat
      const lng = e.latlng.lng
      // 复用通用的位置确认方法
      this.showLocationConfirmation(lat, lng, false)
    },
    // 确认位置选择
    confirmLocationSelection(lat, lng, address) {
      // 关闭 popup
      if (this.clickedLocationMarker) {
        this.clickedLocationMarker.closePopup()
      }

      Toast.success(this.$t('toast_location_confirmed'))

      // 触发位置选择事件
      this.$emit('location-selected', {
        lat: lat,
        lng: lng,
        address: address
      })
    },
    // 搜索位置
    async searchLocation() {
      if (!this.searchQuery || !this.searchQuery.trim()) {
        Toast(this.$t('toast_enter_search'))
        return
      }

      try {
        // 使用后端代理访问 Nominatim API（后端缓存，无需 API Key）
        const response = await axios.get('/api/map/geocode', {
          params: {
            q: this.searchQuery,
            limit: 5
          }
        })

        // 检查返回状态码
        if (response.data.code !== 0) {
          Toast(this.$t('toast_search_failed'))
          this.searchResults = []
          this.showSearchResults = false
          return
        }

        // 检查是否有搜索结果
        console.log('response.data', response.data)
        const results = response.data.data || []
        if (results.length > 0) {
          this.searchResults = results
          this.showSearchResults = true
        } else {
          Toast(this.$t('toast_no_locations'))
          this.searchResults = []
          this.showSearchResults = false
        }
      } catch (error) {
        console.error('Search failed:', error)
        Toast.fail(this.$t('toast_search_error'))
        this.searchResults = []
        this.showSearchResults = false
      }
    },
    // 选择搜索结果
    selectSearchResult(result) {
      const lat = result.lat
      const lng = result.lon

      // 移动地图到选中的位置
      this.map.setView([lat, lng], 16)

      // 隐藏搜索结果
      this.showSearchResults = false
      this.searchQuery = ''

      Toast.success(this.$t('toast_located_at', {address: result.display_name}))

      // 复用通用的位置确认方法，传入已知的地址避免二次查询
      this.showLocationConfirmation(lat, lng, 'search', result.display_name)
    },
    // 获取当前选中的位置
    getSelectedLocation() {
      if (this.clickedLat && this.clickedLng) {
        return {
          lat: this.clickedLat,
          lng: this.clickedLng
        }
      }
      return null
    },
    // 获取用户当前位置
    getUserCurrentLocation() {
      if (this.userLat && this.userLng) {
        return {
          lat: this.userLat,
          lng: this.userLng
        }
      }
      return null
    },
    // 设置地图中心
    setMapCenter(lat, lng, zoom = 15) {
      if (this.map) {
        this.map.setView([lat, lng], zoom)
      }
    },
    // 强制初始化地图（供外部调用，如弹窗打开后）
    forceInitMap() {
      this.$nextTick(() => {
        // 如果地图已存在，刷新尺寸
        if (this.map) {
          this.map.invalidateSize()
          return
        }
        // 如果地图未初始化，主动初始化
        const container = this.$refs.mapContainer
        if (container && container.offsetWidth > 0 && container.offsetHeight > 0) {
          this.initMap()
        }
      })
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
:deep(.leaflet-container) {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
}

:deep(.user-location-marker) {
  background-color: transparent;
}

:deep(.click-location-marker) {
  background-color: transparent;
}

:deep(.search-location-marker) {
  background-color: transparent;
}

/* 根容器 - 高度由外部传入的 height prop 决定 */
.map-location-picker {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
}

/* 搜索框区域 */
.search-box {
  position: relative;
  margin: 0px 10px 10px 0px;
  flex-shrink: 0;
}

/* 搜索结果列表 */
.search-results {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  max-height: 300px;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.search-result-item {
  padding: 12px;
  border: 1px solid #E0DDD8;
  cursor: pointer;
  transition: background 0.2s;
}

/* 地图容器 - 占据剩余空间（排除搜索框） */
.map-container {
  flex: 1;
  min-height: 0;
}
</style>



// WEBPACK FOOTER //
// src/components/MapLocationPicker.vue